import { useCallback, useEffect, useRef } from "react";
import {
  MAX_CANVAS_IMAGE_BYTES,
  estimateDataUrlBytes,
  findUploadableImageElements,
} from "../../../features/canvas/canvasImageRefs";
import {
  preloadCanvasImage,
  uploadCanvasImageFromDataUrl,
} from "../../../api/canvasImages.api";

const SYNC_DEBOUNCE_MS = 700;
const MAX_UPLOAD_ATTEMPTS = 3;
const IMAGE_SYNC_WAIT_TIMEOUT_MS = 20000;
const IMAGE_SYNC_POLL_INTERVAL_MS = 150;
const IMAGE_TOO_LARGE_MESSAGE = "Canvas image exceeds the 10 MB upload limit";
const IMAGE_SYNC_FAILED_MESSAGE = "Could not upload canvas image";

const sleep = (durationMs) =>
  new Promise((resolve) => setTimeout(resolve, durationMs));

const createSyncState = () => ({
  attemptsBySrc: new Map(),
  inFlightSrcs: new Set(),
  refBySrc: new Map(),
});

const hasExhaustedAttempts = ({ src, syncState }) =>
  (syncState.attemptsBySrc.get(src) ?? 0) >= MAX_UPLOAD_ATTEMPTS;

const createElementUploader = ({ engine, isDisposedRef, reportError, syncState }) => {
  const recordFailure = (src) => {
    const attempts = (syncState.attemptsBySrc.get(src) ?? 0) + 1;
    syncState.attemptsBySrc.set(src, attempts);
    if (attempts >= MAX_UPLOAD_ATTEMPTS) reportError(IMAGE_SYNC_FAILED_MESSAGE);
  };

  return async ({ id, src }) => {
    syncState.inFlightSrcs.add(src);
    try {
      const assetRef =
        syncState.refBySrc.get(src) ?? (await uploadCanvasImageFromDataUrl(src));
      syncState.refBySrc.set(src, assetRef);
      await preloadCanvasImage(assetRef);
      if (!isDisposedRef.current) {
        engine.replaceImageSource({ elementId: id, src: assetRef });
      }
      syncState.attemptsBySrc.delete(src);
    } catch (error) {
      console.error(`Canvas image sync failed for element ${id}:`, error);
      recordFailure(src);
    } finally {
      syncState.inFlightSrcs.delete(src);
    }
  };
};

const createSyncPassRunner = ({ engine, reportError, syncState, uploadElement }) => {
  const isSyncCandidate = ({ src }) =>
    !syncState.inFlightSrcs.has(src) &&
    !hasExhaustedAttempts({ src, syncState });

  // Candidacy is checked per element at call time: uploads mark their src
  // in-flight synchronously, so duplicated elements sharing one pasted src
  // upload the bytes only once.
  const syncElement = (element) => {
    if (!isSyncCandidate(element)) return;
    if (estimateDataUrlBytes(element.src) > MAX_CANVAS_IMAGE_BYTES) {
      syncState.attemptsBySrc.set(element.src, MAX_UPLOAD_ATTEMPTS);
      reportError(IMAGE_TOO_LARGE_MESSAGE);
      return;
    }
    uploadElement(element);
  };

  return () =>
    findUploadableImageElements(engine.getState().elements).forEach(syncElement);
};

/**
 * Moves inline data-URL images out of the scene: uploads each to blob storage,
 * verifies the delivery URL loads, then swaps the element src to its stable
 * `netpad-blob:` ref (history-transparent). Covers fresh pastes, legacy base64
 * scenes, and offline notes that later switch to cloud saving. Repeated
 * failures stop after a few attempts and surface through `onSyncError`; the
 * element then simply keeps its data URL.
 * @param {{ engine: object, isEnabled: boolean, onSyncError?: (message: string) => void, value: string }} params
 * @returns {{ waitForImageSync: () => Promise<boolean> }}
 */
export const useCanvasImageSync = ({ engine, isEnabled, onSyncError, value }) => {
  const syncStateRef = useRef(null);
  if (!syncStateRef.current) syncStateRef.current = createSyncState();
  const onSyncErrorRef = useRef(onSyncError);
  const scheduleRef = useRef(null);
  const runPassRef = useRef(null);
  const isEnabledRef = useRef(isEnabled);
  isEnabledRef.current = isEnabled;

  useEffect(() => {
    onSyncErrorRef.current = onSyncError;
  }, [onSyncError]);

  // Drives upload passes without the debounce until nothing is pending, so
  // explicit saves can flush images first. Resolves true when every image
  // holds a blob ref, false when some kept their data URLs (failures, the
  // size cap, or the timeout) — saving those is still better than waiting
  // forever.
  const waitForImageSync = useCallback(async () => {
    if (!isEnabledRef.current) return true;
    const syncState = syncStateRef.current;
    const getPendingUploads = () =>
      findUploadableImageElements(engine.getState().elements).filter(
        ({ src }) => !hasExhaustedAttempts({ src, syncState })
      );
    const deadline = Date.now() + IMAGE_SYNC_WAIT_TIMEOUT_MS;

    while (getPendingUploads().length) {
      if (Date.now() >= deadline) return false;
      runPassRef.current?.();
      await sleep(IMAGE_SYNC_POLL_INTERVAL_MS);
    }
    return findUploadableImageElements(engine.getState().elements).length === 0;
  }, [engine]);

  useEffect(() => {
    if (!isEnabled) return undefined;

    const isDisposedRef = { current: false };
    let timeoutId = null;
    const syncState = syncStateRef.current;
    const reportError = (message) => onSyncErrorRef.current?.(message);
    const uploadElement = createElementUploader({
      engine,
      isDisposedRef,
      reportError,
      syncState,
    });
    const runSyncPass = createSyncPassRunner({
      engine,
      reportError,
      syncState,
      uploadElement,
    });

    const scheduleSyncPass = () => {
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        timeoutId = null;
        runSyncPass();
      }, SYNC_DEBOUNCE_MS);
    };

    scheduleRef.current = scheduleSyncPass;
    runPassRef.current = runSyncPass;
    const unsubscribe = engine.subscribeScene(scheduleSyncPass);
    scheduleSyncPass();

    return () => {
      isDisposedRef.current = true;
      scheduleRef.current = null;
      runPassRef.current = null;
      unsubscribe();
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [engine, isEnabled]);

  // loadSnapshot() intentionally does not notify scene listeners, so viewing
  // another note would otherwise never trigger a pass for its legacy images.
  useEffect(() => {
    scheduleRef.current?.();
  }, [value]);

  return { waitForImageSync };
};
